import jsPDF from 'jspdf';
import i18n from '../../../i18n';
import { SceneWithUserNames } from '../../../types/scene.types';
import { BreakdownInfo } from '../../../pages/Home/types';
import { setFont, registerFont, SupportedLanguage } from '../PDFExporter';
import { formatDayNightForExport, formatInteriorExteriorForExport } from '../exportSceneFieldFormat';
import { ElementType, ElementInfo } from '../../../types/element.types';
import { ElementDataCache, ElementRelationDataCache } from '../../../types/cache.types';
import { compareSceneCodes, SceneCodeParts } from '../../../pages/Breakdown/BreakdownMain/Utils/SceneCodeUtils';
import { sortCastItemsByNO, compareExportNamesDigitLetterPinyin } from '../../../utils/elementSortUtils';

const t = (key: string, opts?: { lng?: string }) => i18n.t(key, opts);

// 字体文件映射
const FONT_FILES = {
  'zh-CN': {
    name: 'AlibabaPuHuiTi'
  },
  'zh-HK': {
    name: 'AlibabaPuHuiTi'
  },
  'en': {
    name: 'AlibabaPuHuiTi'
  }
};

// 由于 jspdf-autotable 的类型问题，我们使用 require 动态导入
// @ts-ignore
const autoTable = require('jspdf-autotable').default;

// 颜色常量（十六进制格式）
const COLORS = {
  BLUE: '#5349AD',        // RGB: [83, 73, 173] - 蓝色背景
  WHITE: '#FFFFFF',       // RGB: [255, 255, 255] - 白色背景
  LIGHT_GRAY: '#EEEEEE',  // RGB: [238, 238, 238] - 场次信息区浅灰（略深于 #F5F5F5 以利区分）
  TEXT_WHITE: 255         // 白色文字（灰度值）
};

// 统一样式常量
const STYLES = {
  HEADER_FONT_SIZE: 8,        // header 字体大小
  HEADER_FONT_STYLE: 'normal', // header 字体样式
  TABLE_FONT_SIZE: 9,          // 表格字体大小（表头和内容）
  CELL_PADDING: 0.8,          // 单元格内边距（缩小60%，从2改为0.8）
  HEADER_Y: 10,               // header Y位置
  TABLE_START_Y: 14           // 表格起始Y位置（上移）
};

export const PDF_SCENES_DETAIL_A4_MARGINS_MM = {
  left: 10,
  right: 10,
  top: STYLES.TABLE_START_Y,
  bottom: 10
} as const;

// 将十六进制颜色转换为 RGB 数组
const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

// 字段映射 - 从全局 i18n 获取
const getFieldMapForLanguage = (language: SupportedLanguage) => ({
  sceneCode: t('export.sceneCode', { lng: language }),
  interiorExterior: t('export.interiorExterior', { lng: language }),
  dayNight: t('export.dayNight', { lng: language }),
  sceneSet: t('export.sceneSet', { lng: language }),
  scenePlotDay: t('export.scenePlotDay', { lng: language }),
  scenePagesNumber: t('export.scenePagesNumber', { lng: language }),
  scenePlot: t('export.scenePlot', { lng: language }),
  sceneLocation: t('export.sceneLocation', { lng: language }),
  cast: t('export.cast', { lng: language })
});

// 排序方式枚举
export type SortBy = 'sceneCode' | 'sceneLocation';

// 检查场景的所有字段是否都为空
const isSceneRowEmpty = (scene: SceneWithUserNames): boolean => {
  // 检查所有导出字段是否都为空
  const hasSceneCode = !!scene.sceneCode?.trim();
  const hasInteriorExterior = !!scene.sceneInteriorExterior;
  const hasDayNight = !!scene.sceneDayNight;
  const hasSceneSet = !!scene.sceneSet?.trim();
  const hasScenePlotDay = !!scene.scenePlotDay?.trim();
  const hasScenePagesNumber = !!scene.scenePagesNumber;
  const hasScenePlot = !!scene.scenePlot?.trim();
  const hasSceneLocation = !!scene.sceneLocation?.trim();
  const hasScenePeriod = !!scene.scenePeriod?.trim();
  
  // 如果所有字段都为空，则返回true（表示该行应该被过滤）
  return !hasSceneCode && 
         !hasInteriorExterior && 
         !hasDayNight && 
         !hasSceneSet && 
         !hasScenePlotDay && 
         !hasScenePagesNumber && 
         !hasScenePlot && 
         !hasSceneLocation && 
         !hasScenePeriod;
};

// 统一的header绘制函数
const addTitleHeader = (
  doc: jsPDF,
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  optionName?: string,
  pageNumber?: number
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const startX = 10; // 左边距
  const titleY = STYLES.HEADER_Y;
  
  // 从 localStorage 获取 FilmContext 存储的影片名称（filmName）
  const filmName = typeof window !== 'undefined' ? localStorage.getItem('selectedFilmName') : null;
  
  // 统一使用 header 字体样式
  doc.setFontSize(STYLES.HEADER_FONT_SIZE);
  setFont(doc, language, 'normal');
  doc.setTextColor(0, 0, 0);

  // 左侧：影片名称 | 分场名称
  const versionLabel = t('export.versionLabel', { lng: language });
  const leftTitleText = `《${filmName || ''}》     ${optionName || ''}              ${versionLabel}${breakdownInfo.breakdownName || ''}`;
  doc.text(leftTitleText, startX, titleY);
  
  // 右侧：剧本版本：XXX | 第 X 页
  const versionText = t('export.scriptVersion', { lng: language });
  const pagePrefix = t('export.pagePrefix', { lng: language });
  const pageSuffix = t('export.pageSuffix', { lng: language });
  
  const labelSeparator = t('common.labelSeparator', { lng: language }) as string;
  const pipeSeparator = t('common.pipeSeparator', { lng: language }) as string;
  let rightTitleText = `${versionText}${labelSeparator}${breakdownInfo.breakdownScriptOverview || ''}`;
  
  // 如果有页码，添加页码信息
  if (pageNumber !== undefined) {
    rightTitleText += `${pipeSeparator}${pagePrefix} ${pageNumber}${pageSuffix ? ' ' + pageSuffix : ''}`;
  }
  
  // 计算右侧文本的X位置（右对齐）
  const rightTextWidth = doc.getTextWidth(rightTitleText);
  const rightX = pageWidth - rightTextWidth - startX;
  doc.text(rightTitleText, rightX, titleY);
};



// 将场次元素按分组规则分类，返回分组文本数组
const groupElements = (elements: any[], language: SupportedLanguage) => {
  const lng = language;
  const itemTypeNameMap: Record<ElementType, string> = {
    [ElementType.FEATURED]: t('elementType.FEATURED', { lng }),
    [ElementType.BACKGROUND]: t('elementType.BACKGROUND', { lng }),
    [ElementType.STUNT_PLAYERS]: t('elementType.STUNT_PLAYERS', { lng }),
    [ElementType.ANIMALS]: t('elementType.ANIMALS', { lng }),
    [ElementType.VEHICLES]: t('elementType.VEHICLES', { lng }),
    [ElementType.ART_PROPS]: t('elementType.ART_PROPS', { lng }),
    [ElementType.WARDROBE]: t('elementType.WARDROBE', { lng }),
    [ElementType.HAIR_MAKEUP]: t('elementType.HAIR_MAKEUP', { lng }),
    [ElementType.SFX_MU]: t('elementType.SFX_MU', { lng }),
    [ElementType.SFX]: t('elementType.SFX', { lng }),
    [ElementType.VFX]: t('elementType.VFX', { lng }),
    [ElementType.CAMERA]: t('elementType.CAMERA', { lng }),
    [ElementType.LIGHTING]: t('elementType.LIGHTING', { lng }),
    [ElementType.SOUND]: t('elementType.SOUND', { lng }),
    [ElementType.ADDL_LABOR]: t('elementType.ADDL_LABOR', { lng }),
    [ElementType.SPECIAL_EQUIPMENT]: t('elementType.SPECIAL_EQUIPMENT', { lng }),
    [ElementType.MISC]: t('elementType.MISC', { lng }),
    [ElementType.NOTE]: t('elementType.NOTE', { lng }),
    [ElementType.CAST]: t('elementType.CAST', { lng })
  };

  // 定义分组映射
  const groupMap: Record<ElementType, number> = {
    // 主要角色不参与分组，单独处理
    [ElementType.CAST]: 0,
    // 分组1：特约演员、群众演员、动作演员
    [ElementType.FEATURED]: 1,
    [ElementType.BACKGROUND]: 1,
    [ElementType.STUNT_PLAYERS]: 1,
    // 分组2：动物、车辆
    [ElementType.ANIMALS]: 2,
    [ElementType.VEHICLES]: 2,
    // 分组3：美术道具
    [ElementType.ART_PROPS]: 3,
    // 分组4：服装、梳头化妆、特效化妆
    [ElementType.WARDROBE]: 4,
    [ElementType.HAIR_MAKEUP]: 4,
    [ElementType.SFX_MU]: 4,
    // 分组5：现场特效
    [ElementType.SFX]: 5,
    // 分组6：电脑特效
    [ElementType.VFX]: 6,
    // 分组7：摄影、灯光、收音、外增人员、特殊器材、其他
    [ElementType.CAMERA]: 7,
    [ElementType.LIGHTING]: 7,
    [ElementType.SOUND]: 7,
    [ElementType.ADDL_LABOR]: 7,
    [ElementType.SPECIAL_EQUIPMENT]: 7,
    [ElementType.MISC]: 7,
    // 分组8：备注
    [ElementType.NOTE]: 8
  };

  // 按分组和类型分类元素
  const groups = new Map<number, Map<ElementType, any[]>>();
  
  elements.forEach(item => {
    // 确保elementType是有效的ElementType枚举值
    const elementType = item.elementType as ElementType;
    const groupId = groupMap[elementType];
    if (groupId > 0) {
      if (!groups.has(groupId)) {
        groups.set(groupId, new Map<ElementType, any[]>());
      }
      const group = groups.get(groupId)!;
      if (!group.has(elementType)) {
        group.set(elementType, []);
      }
      group.get(elementType)?.push(item);
    }
  });

  // 生成格式化的分组文本
  const formattedGroups: string[] = [];
  
  // 定义每个分组内的类型排序顺序
  const groupTypeOrder: Record<number, ElementType[]> = {
    1: [
      ElementType.FEATURED,        // 特约角色
      ElementType.BACKGROUND, // 群众演员
      ElementType.STUNT_PLAYERS           // 动作演员
    ],
    2: [
      ElementType.ANIMALS,               // 动物
      ElementType.VEHICLES               // 车辆
    ],
    4: [
      ElementType.WARDROBE,              // 服装
      ElementType.HAIR_MAKEUP,               // 梳头化妆
      ElementType.SFX_MU            // 特效化妆
    ],
    7: [
      ElementType.CAMERA,               // 摄影
      ElementType.LIGHTING,              // 灯光
      ElementType.SOUND,                // 收音
      ElementType.ADDL_LABOR,       // 外增人员
      ElementType.SPECIAL_EQUIPMENT,     // 特殊器材
      ElementType.MISC                 // 其他
    ]
  };
  
  // 按分组ID顺序处理
  for (let i = 1; i <= 8; i++) {
    if (groups.has(i)) {
      const group = groups.get(i)!;
      if (group.size > 0) {
        let groupText = '';
        
        // 按类型顺序处理分组内的元素
        // 如果分组有定义的排序顺序，使用定义的顺序；否则使用字典序排序
        let itemTypes: ElementType[];
        if (groupTypeOrder[i]) {
          // 使用定义的顺序，但只包含实际存在的类型
          itemTypes = groupTypeOrder[i].filter(type => group.has(type));
        } else {
          // 其他分组使用字典序排序
          itemTypes = Array.from(group.keys()).sort();
        }
        
        itemTypes.forEach((itemType, index) => {
          const items = group.get(itemType)!;
          if (items.length > 0) {
            const typeName = itemTypeNameMap[itemType];
            const colon = t('export.elementTypeListColon', { lng });
            let typeText = `${typeName}${colon}`;
            
            // 对元素按名称排序：数字、英文、拼音（与 elementSortUtils 导出规则一致）
            const sortedItems = [...items].sort((a, b) => {
              const nameA = (a.elementName || a.elementAbbr || '').trim();
              const nameB = (b.elementName || b.elementAbbr || '').trim();
              return compareExportNamesDigitLetterPinyin(nameA, nameB);
            });

            // 格式化每个元素
            const itemTexts = sortedItems.map(item => {
              const quantity = item.elementQtyInScene || 1;
              const elementName = (item.elementName || item.elementAbbr || '').trim();
              // 只有分组1和分组2需要显示数量
              if (i === 1 || i === 2) {
                return `${elementName}*${quantity}`;
              } else {
                return elementName;
              }
            });
            
            typeText += itemTexts.join(' / ');
            
            // 每个类型一行，最后一个类型不需要换行
            if (index < itemTypes.length - 1) {
              typeText += '\n';
            }
            
            groupText += typeText;
          }
        });
        
        if (groupText) {
          formattedGroups.push(groupText);
        }
      }
    }
  }
  
  return formattedGroups;
};

// 导出分场详情A4 PDF
export const exportScenesDetailA4 = async (
  doc: jsPDF, 
  scenes: SceneWithUserNames[], 
  breakdownInfo: BreakdownInfo,
  language: SupportedLanguage,
  secondaryLanguage: SupportedLanguage | undefined,
  sortBy: SortBy = 'sceneCode',
  optionName?: string,
  breakdownId?: string,
  getElementCache?: (breakdownId: string) => ElementDataCache | null,
  getElementRelationCache?: (breakdownId: string) => ElementRelationDataCache | null
): Promise<jsPDF> => {
  // 设置页面为竖向A4
  doc = new jsPDF('p', 'mm', 'a4');
  
  // 重新注册字体（因为创建了新实例）
  const fontRegistered = await registerFont(doc, language);
  if (!fontRegistered) {
    console.warn(`字体注册失败，将使用默认字体。语言: ${language}`);
  }
  
  // 设置字体
  setFont(doc, language);
  
  // 在第一页绘制header
  addTitleHeader(doc, breakdownInfo, language, optionName, 1);
  
  // 准备表格数据
  // 创建场号解析缓存，提升排序性能（compareSceneCodes 内部会自动填充缓存）
  const parseCache = new Map<string, SceneCodeParts | null>();
  
  // 根据排序方式排序
  const sortedScenes = scenes
    .filter(scene => !isSceneRowEmpty(scene)) // 过滤所有字段都为空的行
    .sort((a, b) => {
      if (sortBy === 'sceneLocation') {
        // 按拍摄地点排序（支持中文拼音排序）
        const locationA = (a.sceneLocation || '').trim();
        const locationB = (b.sceneLocation || '').trim();
        
        // 如果两个都是空，按场号排序（次要规则，使用复杂场号排序规则）
        if (!locationA && !locationB) {
          return compareSceneCodes(a, b, parseCache);
        }
        // 如果只有一个是空，空的排在后面
        if (!locationA) return 1;
        if (!locationB) return -1;
        
        // 两个都不为空，按拍摄地点排序（使用 localeCompare 并指定 'zh-CN' locale 支持中文拼音排序）
        const locationCompare = locationA.localeCompare(locationB, 'zh-CN', { numeric: true, sensitivity: 'base' });
        // 如果拍摄地点相同，按场号排序（次要规则，使用复杂场号排序规则）
        if (locationCompare === 0) {
          return compareSceneCodes(a, b, parseCache);
        }
        return locationCompare;
      } else {
        // 默认按场号排序（使用复杂场号排序规则）
        const codeCompare = compareSceneCodes(a, b, parseCache);
        // 如果场号相同，按剧情日排序
        if (codeCompare === 0) {
          return (a.scenePlotDay || '').localeCompare(b.scenePlotDay || '');
        }
        return codeCompare;
      }
    });
  
  // 从缓存获取 elements 和 elementRelations
  const elementsBySceneId = new Map<string, ElementInfo[]>();
  
  if (breakdownId && getElementCache && getElementRelationCache) {
    const elementCache = getElementCache(breakdownId);
    const elementRelationCache = getElementRelationCache(breakdownId);
    
    const allElements = elementCache?.elements || [];
    const allRelations = elementRelationCache?.elementRelations || [];
    
    // 创建 elementID 到 ElementInfo 的映射
    const elementMap = new Map<string, ElementInfo>();
    allElements.forEach(item => {
      elementMap.set(item.elementID, item);
    });
    
    // 根据关系，将 elements 按 sceneID 分组，并合并关系中的数量信息
    allRelations.forEach(relation => {
      const element = elementMap.get(relation.elementID);
      if (element) {
        // 创建一个副本，并添加关系中的数量信息
        const enrichedItem = {
          ...element,
          elementQtyInScene: relation.elementQtyInScene || 1
        };
        const existing = elementsBySceneId.get(relation.sceneID) || [];
        elementsBySceneId.set(relation.sceneID, [...existing, enrichedItem]);
      }
    });
  }

  const getCastColumnTextForScene = (sceneId: string): string => {
    const elements = elementsBySceneId.get(sceneId) || [];
    
    if (!Array.isArray(elements) || elements.length === 0) {
      return '';
    }
    
    const castItems = elements.filter(
      (item: ElementInfo) => item.elementType === ElementType.CAST
    );
    
    const sortedCastItems = sortCastItemsByNO(castItems);
    
    const roleNames = sortedCastItems
      .map((item: ElementInfo) => item.elementAbbr || item.elementName || '')
      .filter(Boolean);
    
    return roleNames.join('/') || '';
  };
  
  // 获取场次元素数据的辅助函数（从缓存获取）
  const getElementsForScene = (sceneId: string): string[] => {
    const elements = elementsBySceneId.get(sceneId) || [];
    
    if (!Array.isArray(elements) || elements.length === 0) {
      return [];
    }
    
    // 按规则对元素分类进行分组
    return groupElements(elements, language);
  };
  
  const castTextBySceneId = new Map<string, string>();
  const elementsMap = new Map<string, string[]>();
  
  sortedScenes.forEach((scene) => {
    const castColumnText = getCastColumnTextForScene(scene.sceneID);
    castTextBySceneId.set(scene.sceneID, castColumnText);
    
    const elements = getElementsForScene(scene.sceneID);
    elementsMap.set(scene.sceneID, elements);
  });
  
  // 表格头 - 使用当前APP语言显示
  const fieldMap = getFieldMapForLanguage(language);
  
  // 获取字体名称，如果字体注册失败则使用默认字体
  const fontName = fontRegistered ? FONT_FILES[language].name : 'helvetica';
  
  // 计算页面可用宽度（A4竖向：210mm，减去左右边距20mm）
  const pageWidthForTable = doc.internal.pageSize.getWidth();
  const margin = PDF_SCENES_DETAIL_A4_MARGINS_MM.left; // 左右边距各10mm
  const tableWidth = pageWidthForTable - (margin * 2); // 可用表格宽度
  
  // 根据百分比计算各列的实际宽度（毫米）
  // 注意：表格实际只有5列（场号、内外、日夜、场景、拍摄地点）
  // 第二行的剧情日、页数、剧情、主要角色分别对应第1、2、3、4列
  const columnWidths = {
    0: tableWidth * 0.10,  // 场号
    1: tableWidth * 0.07,  // 内外/剧情日
    2: tableWidth * 0.07,  // 日夜/页数
    3: tableWidth * 0.38,  // 场景/剧情
    4: tableWidth * 0.38   // 拍摄地点（第一行）/ 主要角色（第二行）
  };
  
  // 构建复杂表头
  const complexHeaders = [
    [
      {
        content: fieldMap.sceneCode, // 场号
        rowSpan: 2, // 合并两行
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.interiorExterior, // 内外
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.dayNight, // 日夜
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.sceneSet, // 场景
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.sceneLocation, // 拍摄地点
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      }
    ],
    [
      // 注意：当第一行有 rowSpan: 2 时，第二行不需要空字符串占位
      // jspdf-autotable 会自动处理 rowSpan，第二行应该只包含剩余的4列
      {
        content: fieldMap.scenePlotDay, // 剧情日（对应第一行的"内外"列）
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.scenePagesNumber, // 页数（对应第一行的"日夜"列）
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.scenePlot, // 剧情（对应第一行的"场景"列）
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      },
      {
        content: fieldMap.cast, // 主要角色（对应第一行的「拍摄地点」列）
        colSpan: 1,
        styles: {
          halign: 'center',
          valign: 'middle',
          // 表头不要背景色 - 设置为白色背景色（透明效果）
          fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
          textColor: 0, // 黑色文字
          fontStyle: 'bold'
          // 边框样式使用grid主题的默认样式，与表格内容一致
        }
      }
    ]
  ];
  
  // 构建表格数据
  let tableData: any[] = [];
  
  sortedScenes.forEach(scene => {
    const elements = elementsMap.get(scene.sceneID) || [];
    
    // 第一行：场号（合并两行）、内外、日夜、场景、拍摄地点
    tableData.push([
      {
        content: scene.sceneCode || '',
        rowSpan: 2,
        styles: {
          halign: 'left',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: formatInteriorExteriorForExport(language, scene.sceneInteriorExterior, secondaryLanguage),
        styles: {
          halign: 'center',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: formatDayNightForExport(language, scene.sceneDayNight, secondaryLanguage),
        styles: {
          halign: 'center',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: scene.sceneSet || '',
        styles: {
          halign: 'left',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: scene.sceneLocation || '',
        styles: {
          halign: 'left',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      }
    ]);
    
    // 第二行：剧情日、页数、剧情、主要角色（不需要空字符串，因为场号有 rowSpan: 2）
    tableData.push([
      {
        content: scene.scenePlotDay || '',
        styles: {
          halign: 'center',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: scene.scenePagesNumber || '',
        styles: {
          halign: 'center',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: scene.scenePlot || '',
        styles: {
          halign: 'left',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      },
      {
        content: castTextBySceneId.get(scene.sceneID) || '',
        styles: {
          halign: 'left',
          valign: 'middle',
          fillColor: hexToRgb(COLORS.LIGHT_GRAY) // 浅灰色背景
        }
      }
    ]);
    
    // 场次元素数据行，每个分组占一行，合并所有列
    if (elements.length > 0) {
      elements.forEach((item: string) => {
        tableData.push([
          {
            content: item,
            colSpan: 5, // 合并所有列
            styles: {
              halign: 'left',
              valign: 'middle',
              fillColor: hexToRgb(COLORS.WHITE) // 白色背景
            }
          },
          '', '', '', '' // 合并后，其他列为空
        ]);
      });
    } else {
      // 若无场次元素分类数据，显示单行提示
      tableData.push([
        {
          content: t('export.breakdownDetailNoCategoryElements', { lng: language }),
          colSpan: 5, // 合并所有列
          styles: {
            halign: 'left',
            valign: 'middle',
            fillColor: [255, 255, 255] // 白色背景
          }
        },
        '', '', '', '' // 合并后，其他列为空
      ]);
    }
  });
  
  // 计算页数总计
  const totalPages = sortedScenes.reduce((sum, scene) => {
    const pages = parseFloat(scene.scenePagesNumber?.toString() || '0') || 0;
    return sum + pages;
  }, 0);
  
  // 添加总计行：合并前2个单元格，合并第3个到最后的单元格，显示在第3个单元格（页数所在列）
  // 注意：这个文件只有5列，页数在第二行的第2列（索引2）
  const totalPageText = t('export.total', { lng: language });
  const pageUnitText = t('export.scenePagesCountUnit', { lng: language });
  const totalRow: any[] = [
    { content: '', colSpan: 2 }, // 合并前2个单元格（索引0-1）
    { content: `${totalPageText} ${totalPages.toFixed(1)} ${pageUnitText}`, colSpan: 3, styles: { halign: 'left', valign: 'middle' } } // 合并第3个到最后的单元格（索引2-4），显示在页数所在列，靠左对齐，最多显示1位小数
  ];
  
  // 使用autoTable生成表格
  doc.setFontSize(11);
  setFont(doc, language, 'normal');
  
  autoTable(doc, {
    head: complexHeaders,
    body: [...tableData, totalRow],
    startY: STYLES.TABLE_START_Y,
    theme: 'grid',
    margin: { left: margin, right: margin, top: STYLES.TABLE_START_Y, bottom: 10 }, // 统一顶部边距，确保所有页面表格位置一致
    tableWidth: tableWidth, // 明确指定表格总宽度
    showHead: 'everyPage', // 在每一页显示表头
    styles: {
      font: fontName,
      fontSize: STYLES.TABLE_FONT_SIZE, // 表格内容字体大小
      cellPadding: STYLES.CELL_PADDING,
      valign: 'middle', // 垂直居中对齐
      halign: 'left', // 水平左对齐
      textColor: 0, // 正文纯黑，纸质打印更清晰
      fillColor: hexToRgb(COLORS.WHITE), // 默认单元格背景色
      lineColor: [0, 0, 0], // 黑色边框（grid主题默认）
      lineWidth: 0.2 // 边框宽度（grid主题默认）
    },
    headStyles: {
      // 表头样式 - 与表格内容边框样式一致
      fillColor: hexToRgb(COLORS.WHITE), // 白色背景，等同于无背景色
      textColor: 0, // 黑色文字
      fontStyle: 'bold',
      font: fontName,
      fontSize: STYLES.TABLE_FONT_SIZE, // 表头字体大小
      valign: 'middle', // 垂直居中对齐
      halign: 'center', // 水平居中对齐
      lineColor: [0, 0, 0], // 黑色边框，与表格内容一致
      lineWidth: 0.2 // 边框宽度，与表格内容一致
    },
    columnStyles: {
      0: { cellWidth: columnWidths[0] },  // 场号
      1: { cellWidth: columnWidths[1] },  // 内外（第一行）/剧情日（第二行）
      2: { cellWidth: columnWidths[2] },  // 日夜（第一行）/页数（第二行）
      3: { cellWidth: columnWidths[3] },  // 场景（第一行）/剧情（第二行）
      4: { cellWidth: columnWidths[4] }   // 拍摄地点（第一行）/主要角色（第二行）
    },
    didParseCell: function(data: any) {
      // 表头样式调整
      if (data.row.section === 'head') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = STYLES.TABLE_FONT_SIZE; // 表头字体大小
        // 表头不要背景色 - 设置为白色背景色（透明效果）
        data.cell.styles.fillColor = hexToRgb(COLORS.WHITE); // 白色背景，等同于无背景色
        data.cell.styles.textColor = 0; // 黑色文字
        data.cell.styles.halign = 'center';
        data.cell.styles.valign = 'middle';
        // 确保表头边框样式与表格内容一致
        data.cell.styles.lineColor = [0, 0, 0]; // 黑色边框
        data.cell.styles.lineWidth = 0.2; // 边框宽度
      }
      // 防止body行被分页截断
      if (data.row.section === 'body') {
        // 为每一行的第一个单元格设置rowPageBreak，防止整行被截断
        if (data.column.index === 0) {
          data.cell.rowPageBreak = 'avoid';
        }
      }
    },
    didDrawCell: function(data: any) {
      // 场次元素行：仅给「类型名称」加下划线（不含冒号与元素名）
      if (data.row.section !== 'body' || data.column.index !== 0) return;
      const lines =
        Array.isArray(data.cell?.text) && data.cell.text.length > 0
          ? data.cell.text.map((x: unknown) => String(x))
          : typeof data.cell?.raw === 'string'
            ? String(data.cell.raw).split('\n')
            : [];
      if (lines.length === 0) return;

      const padLeft = typeof data.cell.padding === 'function' ? data.cell.padding('left') : STYLES.CELL_PADDING;
      const padTop = typeof data.cell.padding === 'function' ? data.cell.padding('top') : STYLES.CELL_PADDING;
      const innerHeight = data.cell.height - padTop - STYLES.CELL_PADDING;
      const lineHeight = innerHeight > 0 ? innerHeight / lines.length : STYLES.TABLE_FONT_SIZE * 0.5;
      const startX = data.cell.x + padLeft;

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      lines.forEach((line: string, idx: number) => {
        const cleanLine = line.trim();
        const colonIndex = Math.max(cleanLine.indexOf('：'), cleanLine.indexOf(':'));
        if (colonIndex <= 0) return;
        const typeLabel = cleanLine.slice(0, colonIndex).trim();
        if (!typeLabel) return;

        const labelWidth = doc.getTextWidth(typeLabel);
        const baselineY = data.cell.y + padTop + lineHeight * (idx + 1) - 0.25;
        doc.line(startX, baselineY, startX + labelWidth, baselineY);
      });
    },
    didDrawPage: function(data: any) {
      // 在每一页绘制 header（第一页已在表格绘制前绘制，这里处理后续页面）
      if (data.pageNumber > 1) {
        addTitleHeader(doc, breakdownInfo, language, optionName, data.pageNumber);
      }
    }
  });
  
  return doc;
};
